import React, { useState, useCallback, useEffect } from 'react';
import Portal from '../Portal.jsx';
import { Button, Modal, Form, Message } from 'semantic-ui-react';
import { EnhancedFormField } from '../defaultClassComponents.jsx';

export default function FilterModal(props) {
  const [errors, setErrors] = useState([]);
  const [name, setName] = useState(props.filter.name || '');
  const [description, setDescription] = useState(
    props.filter.description || '',
  );
  const nameChange = useCallback((event) => setName(event.target.value), []);
  const descriptionChange = useCallback(
    (event) => setDescription(event.target.value),
    [],
  );
  const onClose = useCallback(() => {
    setName(props.filter.name || '');
    setDescription(props.filter.description || '');
    setErrors([]);
    props.close();
  }, [props.close, props.filter.name, props.filter.description]);

  useEffect(() => {
    setName(props.filter.name || '');
    setDescription(props.filter.description || '');
  }, [props.filter.name, props.filter.description]);

  const errorFor = (key) => {
    if (!errors || !Object.prototype.hasOwnProperty.call(errors, key)) {
      return false;
    }

    return errors[key];
  };

  const renderErrors = (key) => {
    const fieldErrors = errorFor(key);

    if (!fieldErrors) {
      return;
    }

    return (
      <div className='error'>
        {fieldErrors.map((error, i) => {
          return (
            <React.Fragment key={`sf_error${i}`}>
              {error}
              <br />
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const otherErrors = () => {
    return Object.keys(errors)
      .filter((key) => key !== 'name' && key !== 'description')
      .map((key) => `${key}: ${errors[key]}`);
  };

  const requestParams = () => {
    const method = props.filter.id ? 'PATCH' : 'POST';
    const csrfToken = document.head.querySelector('meta[name="csrf-token"]');
    let headers = { 'Content-Type': 'application/json' },
      filterParams = {
        name: name,
        description: description,
      };

    if (props.query) {
      filterParams.query = props.query.trim();
    }

    // Needs to be checked because in test environment there's no csrf-token meta tag
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken.content;
    }

    return {
      method: method,
      body: JSON.stringify({ filter: filterParams }),
      headers: headers,
    };
  };

  const saveAction = () => {
    const filterUrl = `/${currentCustomer}/filters/${props.filter.id || ''}`;

    fetch(filterUrl, requestParams())
      .then((response) => response.json())
      .then((data) => {
        if (data.errors) {
          setErrors(data.errors);
          return;
        }
        onClose();
        props.onSave(data.filter);
      })
      .catch((error) => {
        setErrors({
          'Unknown error':
            'It was not possible to save the saved filter due to an unknown error',
        });
        console.error(error); // eslint-disable-line no-console
      });
  };

  return (
    <Portal node={document.body}>
      <Modal
        open={props.isOpen}
        onClose={props.close}
      >
        <Modal.Header>
          {props.filter.id ? 'Edit' : 'Create'} Filter
        </Modal.Header>
        <Modal.Content>
          <If condition={otherErrors().length > 0}>
            <Message error>
              {otherErrors().map((error, i) => (
                <p key={`sfg_error${i}`}>{error}</p>
              ))}
            </Message>
          </If>
          <Form>
            <EnhancedFormField
              required
              error={errorFor('name') !== false}
            >
              <label>Name</label>
              <input
                value={name}
                onChange={nameChange}
              />
              {renderErrors('name')}
            </EnhancedFormField>
            <EnhancedFormField error={errorFor('description') !== false}>
              <label>Description</label>
              <textarea
                value={description}
                onChange={descriptionChange}
              />
              {renderErrors('description')}
            </EnhancedFormField>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={'Cancel'}
            icon={'remove'}
            onClick={onClose}
          />
          <Button
            primary
            content={'Save'}
            icon={'save'}
            onClick={saveAction}
          />
        </Modal.Actions>
      </Modal>
    </Portal>
  );
}

FilterModal.defaultProps = {
  onSave: () => {},
};
